import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {Controller, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {createNkBranch, fetchNkBranch, updateNkBranch} from 'api';
import {getAddressesSuggestList, getAddressInfoFromString} from 'api/geo';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Checkbox} from 'components/ui-kit/Checkbox';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {AsyncSelect} from 'components/ui-kit/Select';
import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {NkBranchRequest} from 'types';
import {Address, AddressInfo} from 'types/geo';

import {BranchBoundsMap} from './BranchBoundsMap';
import {
    ActionButton,
    Actions,
    Field,
    Fields,
    Form,
    MapLabel,
    MapSection,
    RequiredMark,
    StatusField,
} from './styled';

interface Props {
    mode: 'create' | 'edit';
}

interface FormValues {
    name: string;
    region: string;
    city: string;
    geoBounds: string;
    isActive: boolean;
}

const defaultValues: FormValues = {
    name: '',
    region: '',
    city: '',
    geoBounds: '',
    isActive: true,
};

const getAddressComponent = (addressInfo: AddressInfo, kinds: string[]) =>
    addressInfo.addressComponents.find(
        ({kind, types}) => kinds.includes(kind) || types.some((type) => kinds.includes(type)),
    )?.name;

const REGION_COMPONENTS = ['province', 'area', 'administrative_area_level_1'];
const CITY_COMPONENTS = ['locality', 'sublocality', 'administrative_area_level_2'];

export const BranchNkForm = ({mode}: Props) => {
    const {t, i18n} = useTranslation();
    const {id} = useParams<{id?: string}>();
    const [isLoading, setIsLoading] = useState(mode === 'edit');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requiredMessage = useCallback(() => t('pages.register.validations.required'), [
        i18n.language,
    ]);
    const schema = useMemo(
        () =>
            yup.object().shape({
                name: yup
                    .string()
                    .trim()
                    .required(requiredMessage()),
                region: yup
                    .string()
                    .trim()
                    .required(requiredMessage()),
                city: yup.string(),
                geoBounds: yup.string(),
                isActive: yup.boolean(),
            }),
        [requiredMessage],
    );
    const {
        control,
        formState: {errors, isValid},
        handleSubmit,
        reset,
        setValue,
        trigger,
        watch,
    } = useForm<FormValues>({resolver: yupResolver(schema), mode: 'all', defaultValues});
    const region = watch('region');

    const getAddressPayload = useCallback(
        (address: string): Address => ({
            address,
            lang: i18n.language.startsWith('ru') ? 'ru' : 'en',
        }),
        [i18n.language],
    );

    const loadAddressOptions = useCallback(
        async (value: string, prefix?: string) => {
            if (!value.trim()) return [];

            try {
                const query = [prefix, value].filter(Boolean).join(', ');
                const suggestions = (await getAddressesSuggestList(getAddressPayload(query))).data;
                return suggestions.map((suggestion) => ({
                    value: suggestion,
                    label: suggestion,
                }));
            } catch (error) {
                showRestError(error as RestError);
                return [];
            }
        },
        [getAddressPayload],
    );

    const selectAddressComponent = useCallback(
        (address: string, field: 'region' | 'city', kinds: string[]) => {
            getAddressInfoFromString(getAddressPayload(address))
                .then((addressInfo) => {
                    const value = addressInfo && getAddressComponent(addressInfo, kinds);
                    setValue(field, value || address, {shouldValidate: true, shouldDirty: true});
                })
                .catch((error) => showRestError(error as RestError));
        },
        [getAddressPayload, setValue],
    );

    useEffect(() => {
        if (mode !== 'edit') return undefined;

        let isMounted = true;
        setIsLoading(true);
        fetchNkBranch(Number(id))
            .then((branch) => {
                if (!isMounted) return;
                reset({
                    name: branch.name || '',
                    region: branch.region || '',
                    city: branch.city || '',
                    geoBounds: branch.geoBounds || '',
                    isActive: branch.isActive !== false,
                });
                return trigger();
            })
            .catch((error) => isMounted && showRestError(error as RestError))
            .finally(() => isMounted && setIsLoading(false));

        return () => {
            isMounted = false;
        };
    }, [id, mode, reset, trigger]);

    const onSubmit = async (values: FormValues) => {
        const request: NkBranchRequest = {
            name: values.name.trim(),
            region: values.region.trim(),
            ...(values.city.trim() && {city: values.city.trim()}),
            ...(values.geoBounds && {geoBounds: values.geoBounds}),
            isActive: values.isActive,
        };

        setIsSubmitting(true);
        try {
            if (mode === 'create') await createNkBranch(request);
            else await updateNkBranch({id: Number(id), request});
            toast.success(
                t(
                    `pages.profile.branchesNk.form.${
                        mode === 'create' ? 'createSuccess' : 'updateSuccess'
                    }`,
                ),
            );
            history.push(ROUTE.BRANCHES_NK);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getError = (name: keyof FormValues) => (errors[name] as FieldError | undefined)?.message;

    return (
        <Page title={t('pages.profile.title')}>
            <BackTo to={ROUTE.BRANCHES_NK}>{t('pages.profile.branchesNk.form.back')}</BackTo>
            <ContentContainer>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Fields>
                        <Field text={getError('name')} isError={!!errors.name}>
                            <span>
                                {t('pages.profile.branchesNk.form.fields.name')}
                                <RequiredMark>*</RequiredMark>
                            </span>
                            <Controller
                                name="name"
                                control={control}
                                render={(props) => (
                                    <Input
                                        {...props}
                                        disabled={isLoading}
                                        hasError={!!errors.name}
                                    />
                                )}
                            />
                        </Field>
                        <Field text={getError('region')} isError={!!errors.region}>
                            <span>
                                {t('pages.profile.branchesNk.form.fields.region')}
                                <RequiredMark>*</RequiredMark>
                            </span>
                            <div style={{paddingBottom: 6}}>
                                <Controller
                                    name="region"
                                    control={control}
                                    render={({name, value, onChange}) => (
                                        <AsyncSelect
                                            name={name}
                                            loadOptions={(query) => loadAddressOptions(query)}
                                            value={value ? {value, label: value} : null}
                                            isClearable
                                            isDisabled={isLoading}
                                            hasError={!!errors.region}
                                            onChange={(option?: {value: string; label: string}) => {
                                                onChange('');
                                                setValue('city', '', {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                });
                                                if (option?.value) {
                                                    selectAddressComponent(
                                                        option.value,
                                                        'region',
                                                        REGION_COMPONENTS,
                                                    );
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </Field>
                        <Field text={getError('city')} isError={!!errors.city}>
                            <span>{t('pages.profile.branchesNk.form.fields.city')}</span>
                            <div style={{paddingBottom: 6}}>
                                <Controller
                                    name="city"
                                    control={control}
                                    render={({name, value, onChange}) => (
                                        <AsyncSelect
                                            name={name}
                                            loadOptions={(query) =>
                                                loadAddressOptions(query, region)
                                            }
                                            value={value ? {value, label: value} : null}
                                            isClearable
                                            isDisabled={isLoading || !region}
                                            hasError={!!errors.city}
                                            onChange={(option?: {value: string; label: string}) => {
                                                onChange('');
                                                if (option?.value) {
                                                    selectAddressComponent(
                                                        option.value,
                                                        'city',
                                                        CITY_COMPONENTS,
                                                    );
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </Field>
                        <StatusField>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({value, onChange}) => (
                                    <Checkbox
                                        checked={value}
                                        disabled={isLoading}
                                        onChange={(event) => onChange(event.target.checked)}
                                    >
                                        {t('pages.profile.branchesNk.form.fields.isActive')}
                                    </Checkbox>
                                )}
                            />
                        </StatusField>
                        <MapSection>
                            <MapLabel>
                                {t('pages.profile.branchesNk.form.fields.geoBounds')}
                            </MapLabel>
                            <Controller
                                name="geoBounds"
                                control={control}
                                render={({value, onChange}) => (
                                    <BranchBoundsMap value={value} onChange={onChange} />
                                )}
                            />
                        </MapSection>
                    </Fields>
                    <Actions>
                        <ActionButton
                            type="button"
                            variant={ButtonVariant.Outline}
                            disabled={isSubmitting}
                            onClick={() => history.push(ROUTE.BRANCHES_NK)}
                        >
                            {t('pages.profile.branchesNk.form.cancel')}
                        </ActionButton>
                        <ActionButton
                            type="submit"
                            disabled={isLoading || isSubmitting || !isValid}
                            isLoading={isSubmitting}
                        >
                            {t(
                                `pages.profile.branchesNk.form.${
                                    mode === 'create' ? 'create' : 'save'
                                }`,
                            )}
                        </ActionButton>
                    </Actions>
                </Form>
            </ContentContainer>
        </Page>
    );
};
